import { isStyledComponent as styledComponent } from 'styled-components';

/**
 * Determine if the Component is a React Class Component
 *
 * @method isClassComponent
 * @param  {Component}       Component The Component to check
 * @return {boolean}
 */
export const isClassComponent = Component =>
    Boolean(Component && Component.prototype && typeof Component.prototype.render === 'function');

/**
 * Determine of the Component is a Styled-Component.
 * Fallbacks to the `isStyledComponent` from 'styled-components'.
 *
 * @method isStyledComponent
 * @param  {Component}        Component The Component to check
 * @return {boolean}
 */
export const isStyledComponent = styledComponent;

/**
 * Determine if the Component is a React Element and HTML DOM type.
 * i.e. 'div', 'hr', 'p', 'h3', etc.
 *
 * @method isDOMTypeComponent
 * @param  {Component}  Component The Component to check
 * @return {boolean}
 */
export const isDOMTypeComponent = Component =>
    Boolean(typeof Component === 'string' && !Component.prototype);

/**
 * Determine if the Component is a React Element and a Composite (Functional) Component.
 * i.e. const Button = ({ width }) => <button width={width}>Hello</button>;
 *
 * @method isFunctionalComponent
 * @param  {Component}     Component The Component to check
 * @return {boolean}
 */
export const isFunctionalComponent = Component => {
    return Boolean(
        typeof Component === 'function' && !(Component.prototype && Component.prototype.render)
    );
};

/**
 * Determine if the Component is a React Component,
 * functional, styled, or Class component
 *
 * @method isReactComponent
 * @param  {Component}  Component The Component to check
 * @return {boolean}
 */
export const isReactComponent = Component =>
    Boolean(
        isFunctionalComponent(Component) ||
            isStyledComponent(Component) ||
            isClassComponent(Component)
    );

/**
 * Assign a value to a ref, supporting both callback refs and ref objects.
 *
 * @method setRef
 * @param  {Function|Object} ref   The ref to assign to
 * @param  {*}               value The value to assign
 */
export const setRef = (ref, value) => {
    if (typeof ref === 'function') {
        ref(value);
    } else if (ref && typeof ref === 'object') {
        ref.current = value;
    }
};

/**
 * Merge several refs (callback refs and/or ref objects) into a single callback
 * ref. Useful when a component both forwards a ref and needs the node itself.
 *
 * @method mergeRefs
 * @param  {...(Function|Object)} refs The refs to merge
 * @return {Function}                  A callback ref that updates every ref
 */
export const mergeRefs = (...refs) => value => {
    refs.forEach(ref => setRef(ref, value));
};

/**
 * Get the top-level `document` object for the given node.
 *
 * @see    {@link https://developer.mozilla.org/en-US/docs/Web/API/Node/ownerDocument}
 * @method getDocumentOwner
 * @param  {node}            node The node the document object for
 * @return {any}
 */
export const getDocumentOwner = node => (node && node.ownerDocument) || document;

/**
 * Get the current container based on it being an Object, or a Functional Component
 *
 * @method getContainer
 * @param  {Object|Function}  container        The React Component
 * @param  {HTMLElement}      defaultContainer The default container
 * @return {HTMLElement}
 */
export const getContainer = (container, defaultContainer) => {
    const el = typeof container === 'function' ? container() : container;

    if (el && typeof el === 'object' && Object.hasOwnProperty.call(el, 'current')) {
        return el.current || defaultContainer;
    }

    // `el` may already be a DOM node; anything else falls back to the default.
    return (el && el.nodeType ? el : null) || defaultContainer;
};

/**
 * Get the anchor element for the given element.
 *
 * @method getAnchorEl
 * @param  {HTMLElement}  element The element to get the anchor for
 * @return {HTMLElement}
 */
export const getAnchorEl = element => (typeof element === 'function' ? element() : element);

/**
 * Is the DOM available?
 *
 * @type {boolean}
 */
export const canUseDOM = Boolean(
    typeof window !== 'undefined' && window.document && window.document.createElement
);
